import { createApiHooks, useGenericGet, useGenericPost } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  Bot,
  BotCreate,
  BotGetFiltersResponse,
  BotGetResponse,
  BotUpdate,
} from '../types/bot.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useMemo } from 'react';
import { useActiveTenant } from '@states/active-tenant.atom';

const useBotBasePath = () => {
  const { activeTenant } = useActiveTenant();

  return useMemo(() => {
    return `/v1/admin/tenants/${activeTenant?.id}/bots`;
  }, [activeTenant?.id]);
};

export const useBotHooks = () => {
  const botBasePath = useBotBasePath();
  const { activeTenant } = useActiveTenant();

  return createApiHooks<Bot, BotGetResponse, BotCreate, BotUpdate>({
    basePath: botBasePath,
    queryKey: ['Bot', 'Tenant', activeTenant!.id],
    apiClient,
  });
};

export const useBotGetFilters = (queryOptions?: any) => {
  const botBasePath = useBotBasePath();
  return useGenericGet<BotGetFiltersResponse>(
    `${botBasePath}/filters`,
    ['Bot', 'filters'],
    apiClient,
    queryOptions
  );
};

// Create a custom hook that returns all the bot hooks
export const useBotApi = () => {
  const botHooks = useBotHooks();

  return {
    useGet: botHooks.useGet,
    useCreate: botHooks.useCreate,
    useUpdate: botHooks.useUpdate,
    useDelete: botHooks.useDelete,
    useList: (params: UseListParams) => botHooks.useList(params),
  };
};

// Export individual hooks for backward compatibility
export const useBotGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useBotApi();
  return useGet(id, queryOptions);
};

export const useBotCreate = (mutationOptions?: any) => {
  const { useCreate } = useBotApi();
  return useCreate(mutationOptions);
};

export const useBotUpdate = (mutationOptions?: any) => {
  const { useUpdate } = useBotApi();
  return useUpdate(mutationOptions);
};

export const useBotDelete = (mutationOptions?: any) => {
  const { useDelete } = useBotApi();
  return useDelete(mutationOptions);
};

export const useBotList = (params: UseListParams) => {
  const { useList } = useBotApi();
  return useList(params);
};
